// Mirrors services/orders/custom_orthotics_labels.py; change them together.

import type { CustomOrthoticsLineItem } from "../__generated__/graphql";

const LIMB_REGIONS = new Set(["UPPER_LIMB", "LOWER_LIMB"]);

// Mirrors `_is_ordered_item`: a limb row orders by body part, because the device
// is not decided until an orthotist evaluates the patient. Typed structurally
// because the read-only surfaces hold the output type, whose bodyParts is not
// nullable.
export const isOrderedCustomOrthoticsItem = (item: {
  region: string;
  productName?: string | null;
  bodyParts?: readonly string[] | null;
}): boolean => {
  if (item.productName?.trim()) {
    return true;
  }
  return LIMB_REGIONS.has(item.region) && !!item.bodyParts?.length;
};

const LIMB_REGION_NAMES: Record<string, string> = {
  UPPER_LIMB: "Upper limb orthosis",
  LOWER_LIMB: "Lower limb orthosis",
};

// Mirrors `_humanize`. Every body part, joint type and laterality value spells
// its own label, so there is nothing to look up. These columns carry no CHECK
// constraint, and this leaves an unmapped value slightly raw rather than throwing
// on every surface that shows the order.
const humanize = (value: string): string =>
  value.replace(/_/g, " ").toLowerCase();

const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

// Typed structurally for the same reason as `isOrderedCustomOrthoticsItem`
// above: the input type's bodyParts is optional, the output type's is not.
export const customOrthoticsLineItemName = (item: {
  region: string;
  productName?: string | null;
  bodyParts?: readonly string[] | null;
}): string => {
  const productName = item.productName?.trim();
  if (productName) {
    return productName;
  }
  const region = LIMB_REGION_NAMES[item.region];
  if (!region) {
    return "Custom orthosis";
  }
  const parts = (item.bodyParts ?? []).map(humanize);
  return parts.length > 0 ? `${region} (${parts.join(", ")})` : region;
};

export const customOrthoticsLineItemJointType = (
  item: Pick<CustomOrthoticsLineItem, "jointType">,
): string | null => {
  if (!item.jointType) {
    return null;
  }
  return capitalize(humanize(item.jointType));
};

export const customOrthoticsLineItemLaterality = (
  item: Pick<CustomOrthoticsLineItem, "laterality">,
): string | null =>
  item.laterality ? capitalize(humanize(item.laterality)) : null;

export const customOrthoticsLineItemQuantity = (
  item: Pick<CustomOrthoticsLineItem, "isMaximumAllowedQuantity" | "quantity">,
): string | null => {
  if (item.isMaximumAllowedQuantity) {
    return "Maximum allowed amount";
  }
  return item.quantity != null ? String(item.quantity) : null;
};

/**
 * The medical-necessity multi-select keys in the LCD's own wording.
 *
 * Shared rather than owned by the form: the provider ticks these boxes and then
 * signs the same criteria on the Progress Note summary. The abbreviated set in
 * services/orders/display_model/custom_orthotics.py fills an internal ops
 * table, not a document anyone signs.
 */
export const CUSTOM_ORTHOTICS_OPTION_LABELS: Record<string, string> = {
  PAIN: "Reduce pain by restricting mobility of the trunk",
  INJURY:
    "Facilitate healing following an injury to the spine or related soft tissues",
  SURGERY:
    "Facilitate healing following a surgical procedure on the spine or related " +
    "soft tissue",
  SUPPORT: "Otherwise support weak spinal muscles and/or a deformed spine",
  NOFIT: "The patient cannot be fit with a prefabricated AFO",
  SIXMO:
    "The condition necessitating the orthosis is expected to persist for more " +
    "than 6 months",
  PLANES:
    "There is a need to control the knee, ankle or foot in more than one plane",
  STATUS:
    "The patient has a documented neurological, circulatory, or orthopedic " +
    "status that requires custom fabricating to prevent tissue injury",
  FRACTURE:
    "The patient has a healing fracture that lacks normal anatomical integrity " +
    "or standard proportions",
  NONE: "None of the above",
};
